import { TEXT_LINK_FOCUS_RING } from "../lib/a11yFocus";
import { useContext, useEffect, useState } from "react";
import PageHeadings from "../components/BaseComponents/PageHeadings/PageHeadings";
import PageTemplate from "../components/BaseComponents/PageTemplate/PageTemplate";
import routes from "../components/Router/routes";
import { Button, InlineAlert, SearchableSelect, SearchInput } from "../components/BaseComponents";
import useApi from "../hooks/useApi";
import useIsViewOnly from "../hooks/useIsViewOnly";
import SimpleSortTable, { TableColumn } from "../components/BaseComponents/SimpleSortTable/SimpleSortTable";
import { centsToCurrency, formatDate, getApiErrorMessage, getCSRFToken } from "../lib/utils";
import { Link } from "react-router-dom";
import Pagination from "../components/BaseComponents/Pagination/Pagination";
import useUserData from "../hooks/useUserData";
import useDataSort from "../hooks/useDataSort";
import usePagination from "../hooks/usePagination";
import useCurrencyFormatter from "../hooks/useCurrencyFormatter";
import { userContext } from "../context/UserContext";
import useCleanup from "../hooks/useCleanup";
import { useDebouncedSearch } from "../hooks/useDebouncedSearch";
import NumericText from "../components/BaseComponents/NumericText/NumericText";

type ProjectSelectProps = {
    projects: { id: string, name: string }[],
    projectId: string | null | undefined,
    projectName: string | null | undefined,
    purchaseId: string;
    projectsUnavailable: boolean;
}
function ProjectSelect({projects, projectId, projectName, purchaseId, projectsUnavailable}: ProjectSelectProps) {
    const [value, setValue] = useState(projectId ?? '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const api = useApi();
    const isViewOnly = useIsViewOnly();

    const updatePurchaseProject = async (newProjectId: string | null) => {
        const previousValue = value;
        setLoading(true);
        setValue(newProjectId ?? '');
        setError('');
        try {
            await api.put(`/singlePurchase/${purchaseId}`, {
                _csrf: await getCSRFToken(),
                purchase: {
                    projectId: newProjectId,
                }
            });
        } catch (err) {
            setValue(previousValue);
            setError(getApiErrorMessage(err, "Failed to update this purchase's project. Please try again."));
        } finally {
            setLoading(false);
        }
    }

    const options: { value: string, label: string, isDisabled?: boolean }[] = [
        { value: '', label: 'None' },
        ...projects.map((project) => ({ value: project.id, label: project.name })),
    ]
    if (value && !options.some((option) => option.value === value)) {
        options.push({ value, label: projectName || 'Unavailable project', isDisabled: true })
    }

    return (
        <div>
            <SearchableSelect
                options={options}
                value={options.find((option) => option.value === value) ?? options[0]}
                isDisabled={loading || isViewOnly || projectsUnavailable}
                onChange={(option) => {
                    updatePurchaseProject(option?.value ?? null)
                }}
            />
            {error && <InlineAlert type='error' className='mt-2'>{error}</InlineAlert>}
        </div>
    )
}

function PurchaseList() {
	const formatCurrency = useCurrencyFormatter();
    const formatNumber = Intl.NumberFormat('en-GB').format;

    
    const updatePurchaseProject = async (purchaseId: string, projectId: string) => {
        await api.put(`/singlePurchase/${purchaseId}`, {
            _csrf: await getCSRFToken(),
            purchase: {
                projectId,
            }
        });
        loadPurchases()
    }

    const columns: TableColumn[] = [
        {
            id: 'purchaseDate',
            label: 'Date',
            renderer: (key, data) => <span>{formatDate(data[key])}</span>,
            sortable: true,
        },
        {
            id: 'price',
            label: 'Price',
            renderer: (key, data) => {
                return (
                    <span>
                        <NumericText value={formatCurrency(Number(centsToCurrency(data.price)))} />
                    </span>
                );
            },
            textAlign: 'right',
            sortable: true,
        },
        {
            id: 'quantity',
            label: 'Quantity',
            renderer: (key, data) => (
                <span>
                    <NumericText value={data.quantity} /> { data.unit }
                </span>
            ),
            textAlign: 'right',
            sortable: true,
        },
        {
            id: 'emission_value',
            label: 'Emissions',
            renderer: (key, data) => {
                return (
                    <span>
                        {
                            data[key]
                                ? (<><NumericText value={formatNumber(data[key])} /> kgCO₂e</>)
                                : '-'
                        }
                    </span>
                );
            },
            textAlign: 'right',
            sortable: true,
        },
        {
            id: 'item_name',
            label: 'Item Name',
            renderer: (key, data) => (
                <Link
                    className={`text-link ${TEXT_LINK_FOCUS_RING}`} to={`/purchases/items/${data['itemId']}`}
                >
                    {data[key]}
                </Link>
            ),
            sortable: true,
        },
        {
            id: 'supplier_name',
            label: 'Supplier',
            renderer: (key, data) => (
                <Link
                    className={`text-link ${TEXT_LINK_FOCUS_RING}`} to={`/purchases/suppliers/${data['supplier_id']}`}
                >
                    {data[key]}
                </Link>
            ),
            sortable: true,
        },
        {
            id: 'item_scope',
            label: 'Scope',
            sortable: true,
        },
    ]
    const api = useApi();
    const userData = useUserData();

    const dataSort = useDataSort('purchaseDate', 'desc');

    const [data, setData] = useState<any[]>([])

    const pagination = usePagination(1, 20);

    const [itemSearchString, setItemSearchString] = useState('');
    const [supplierSearchString, setSupplierSearchString] = useState('');
    const [debouncedItemSearchString, setDebouncedItemSearchString] = useState('');
    const [debouncedSupplierSearchString, setDebouncedSupplierSearchString] = useState('');

    // Debounce each search box independently so typing doesn't fire an API request on every keystroke
    const { searchInProgress: itemSearchInProgress, flush: flushItemSearch } = useDebouncedSearch(itemSearchString, () => setDebouncedItemSearchString(itemSearchString))
    const { searchInProgress: supplierSearchInProgress, flush: flushSupplierSearch } = useDebouncedSearch(supplierSearchString, () => setDebouncedSupplierSearchString(supplierSearchString))
    const [loading, setLoading] = useState(false);

    const { checkAccess } = useContext(userContext);
    const [projects, setProjects] = useState<any[]>([]);
    const [projectId, setProjectId] = useState<any>('');
    const [projectsLoading, setProjectsLoading] = useState(true);
    const [projectsError, setProjectsError] = useState('');
    const [projectsRetry, setProjectsRetry] = useState(0);
    const [loadError, setLoadError] = useState('');



    if(checkAccess('company.projects')) {
        columns.push({
            id: 'project_name',
            label: 'Project',
            renderer: (key, data) => {
                const projectId = data['projectId'];
                const purchaseId = data['id']
                return <ProjectSelect
                    key={purchaseId}
                    projectId={projectId}
                    projectName={data['project_name']}
                    projects={projects}
                    purchaseId={purchaseId}
                    projectsUnavailable={projectsLoading || Boolean(projectsError)}
                />

                // return (
                //     <select
                //         className="rounded text-charcoal-900"
                //         value={projectId}
                //         onChange={(e) => {
                //             updatePurchaseProject(purchaseId, e.target.value)
                //         }}
                //         disabled={loadingPurchaseIds.includes(purchaseId)}
                //     >
                //         <option value="">-</option>
                //         { projects.map(project => {
                //             return (
                //                 <option key={project.id} value={project.id}>
                //                     { project.name}
                //                 </option>
                //             )
                //         })}
                //     </select>
                // );
            },
            sortable: true,
        })
    }

    useEffect(() => {
        if(!checkAccess('company.projects')) {
            setProjectsLoading(false);
            return;
        }
        let cancelled = false;
        setProjectsLoading(true);
        setProjectsError('');
        const run = async () => {
            try {
                const results = await api.get('/projects?sortKey=name');
                if (!cancelled) {
                    setProjects(results.data.rows);
                }
            } catch (err) {
                if (!cancelled) {
                    setProjects([]);
                    setProjectsError(getApiErrorMessage(err, 'Failed to load projects. Please try again.'));
                }
            } finally {
                if (!cancelled) {
                    setProjectsLoading(false);
                }
            }
        }
        run();
        return () => {
            cancelled = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectsRetry])
    
    const loadPurchases = useCleanup(() => {
        let cancelled = false;
        const run = async () => {
            setLoading(true);
            setLoadError('');
            try {
                const qs = new URLSearchParams({
                    ...pagination.params,
                    ...dataSort.params,
                    projectId: projectId,
                    itemName: debouncedItemSearchString,
                    supplierName: debouncedSupplierSearchString,
                })
                const result = await api.get('/purchaseList?' + qs.toString());
                if (!cancelled) {
                    setData(result.data.data)
                    pagination.readMeta(result.data.meta);
                }
            } catch (err) {
                if (!cancelled) {
                    setData([]);
                    setLoadError(getApiErrorMessage(err, 'Failed to load purchases. Please try again.'));
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        run();
        return () => {
            cancelled = true;
        }
    });

    useEffect(
        loadPurchases, [
        // eslint-disable-next-line react-hooks/exhaustive-deps
        pagination.params,
        dataSort.params,
        debouncedItemSearchString,
        debouncedSupplierSearchString,
        projectId,
    ])

    return (
        <PageTemplate>
            <PageHeadings crumbs={[{ label: 'Purchases', route: routes.companyPurchases.path }, { label: 'List' }]} />
            <div className="flex space-x-4 my-4">
                <SearchInput
                    name="itemSearchString"
                    label="Item Name"
                    value={itemSearchString}
                    onChange={e => setItemSearchString(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === 'Enter') {
                            flushItemSearch()
                        }
                    }}
                    onSearch={flushItemSearch}
                    onClear={() => {
                        setItemSearchString('')
                        flushItemSearch()
                    }}
                    isLoading={itemSearchInProgress}
                    placeholder="Search by item name"
                />
                <SearchInput
                    name="supplierSearchString"
                    label="Supplier Name"
                    value={supplierSearchString}
                    onChange={e => setSupplierSearchString(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === 'Enter') {
                            flushSupplierSearch()
                        }
                    }}
                    onSearch={flushSupplierSearch}
                    onClear={() => {
                        setSupplierSearchString('')
                        flushSupplierSearch()
                    }}
                    isLoading={supplierSearchInProgress}
                    placeholder="Search by supplier name"
                />
                { userData.access?.company.permissions.projects && (
                    <div>
                        <label className="text-charcoal-100 block">Project</label>
                        <SearchableSelect
                            options={[{ value: '', label: 'any' }, ...projects.map((project) => ({ value: project.id, label: project.name }))]}
                            value={[{ value: '', label: 'any' }, ...projects.map((project) => ({ value: project.id, label: project.name }))]
                                .find((option) => option.value === projectId)}
                            isDisabled={projectsLoading || Boolean(projectsError)}
                            onChange={(option) => setProjectId(option?.value ?? '')}
                        />
                    </div>
                )}
            </div>
            { projectsError && (
                <div className='mb-4'>
                    <InlineAlert type='error' className=''>{ projectsError }</InlineAlert>
                    <Button type='Secondary' text='Retry loading projects' additionalClass='mt-4' onClick={() => setProjectsRetry((count) => count + 1)} />
                </div>
            )}
            { loadError && (
                <InlineAlert type='error' className='mb-4'>{ loadError }</InlineAlert>
            )}
            <SimpleSortTable
                isLoading={loading}
                defaultSorting={dataSort.defaultSorting}
                onSortPress={dataSort.onChange}
                data={data}
                columns={columns}
            />

            <Pagination
                isLoading={loading}
                pagination={ pagination.pagination}
                onPageChange={pagination.onPageChange}
            />
        </PageTemplate>
    )
}

export default PurchaseList;
